import { Download, ExternalLink } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'

import { HubspotCoverageItem } from '@/api/organization-hubspot-client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Icons } from '@/components/ui/icons'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { RoutePaths } from '@/routes/routes'
import { useQueryGetOrganizationHubspotCoverage } from '@/services/hubspot.service'

const csvEscape = (value: unknown) => {
  const text = value === null || value === undefined ? '' : String(value)

  return `"${text.replace(/"/g, '""')}"`
}

const downloadCsv = (filename: string, rows: HubspotCoverageItem[]) => {
  const headers = [
    'org_id',
    'name',
    'organization_use',
    'parent_production_org_id',
    'hubspot_company_id',
    'inherited_parent_hubspot_company_id',
    'exact_match_company_id',
    'exact_match_company_name',
    'exact_match_domain'
  ]
  const csv = [
    headers.join(','),
    ...rows.map((row) =>
      [
        row.org_id,
        row.name,
        row.organization_use,
        row.parent_production_org_id,
        row.hubspot_company_id,
        row.inherited_parent_hubspot_company_id,
        row.exact_hubspot_match?.company_id,
        row.exact_hubspot_match?.company_name,
        row.exact_hubspot_match?.domain
      ]
        .map(csvEscape)
        .join(',')
    )
  ].join('\n')

  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
  const link = document.createElement('a')

  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

const SummaryTile = ({ label, value }: { label: string; value: number }) => (
  <Card className="bg-background">
    <CardHeader className="p-4 pb-1">
      <CardTitle className="text-sm text-muted-foreground">{label}</CardTitle>
    </CardHeader>
    <CardContent className="p-4 pt-0 text-xl font-semibold">
      {value}
    </CardContent>
  </Card>
)

const CoverageTable = ({
  title,
  rows,
  filename
}: {
  title: string
  rows: HubspotCoverageItem[]
  filename: string
}) => (
  <Card className="bg-background">
    <CardHeader className="flex-row items-center justify-between space-y-0">
      <CardTitle className="text-lg">{title}</CardTitle>
      <Button
        disabled={!rows.length}
        onClick={() => downloadCsv(filename, rows)}
        size="sm"
        variant="outline"
      >
        <Download className="mr-2 h-4 w-4" />
        Export
      </Button>
    </CardHeader>
    <CardContent>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Organization</TableHead>
            <TableHead>Use</TableHead>
            <TableHead>Parent</TableHead>
            <TableHead>HubSpot</TableHead>
            <TableHead>Suggested match</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length ? (
            rows.slice(0, 100).map((row) => (
              <TableRow key={row.org_id}>
                <TableCell>
                  <Link
                    className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                    to={`${RoutePaths.ORGANIZATIONS}/${row.org_id}`}
                  >
                    {row.name || row.org_id}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                  <div className="text-xs text-muted-foreground">
                    {row.org_id}
                  </div>
                </TableCell>
                <TableCell>{row.organization_use}</TableCell>
                <TableCell>{row.parent_production_org_id || '-'}</TableCell>
                <TableCell>
                  {row.hubspot_company_id ||
                    row.inherited_parent_hubspot_company_id ||
                    '-'}
                </TableCell>
                <TableCell>
                  {row.exact_hubspot_match?.company_id ? (
                    <>
                      <div>{row.exact_hubspot_match.company_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {row.exact_hubspot_match.company_id}
                        {row.exact_hubspot_match.domain
                          ? ` · ${row.exact_hubspot_match.domain}`
                          : ''}
                      </div>
                    </>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                className="py-8 text-center text-muted-foreground"
                colSpan={5}
              >
                No organizations in this group.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {rows.length > 100 && (
        <div className="pt-3 text-xs text-muted-foreground">
          Showing first 100 rows. Export CSV for the full list.
        </div>
      )}
    </CardContent>
  </Card>
)

const OrganizationHubspotCoverage = () => {
  const { data, isLoading, error } = useQueryGetOrganizationHubspotCoverage()
  const navigate = useNavigate()

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Icons.spinner className="h-5 w-5" />
        <span className="text-sm text-muted-foreground">
          Loading HubSpot coverage...
        </span>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="text-sm text-destructive">
        Could not load HubSpot coverage.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-primary">
            HubSpot Coverage
          </h1>
          <p className="text-sm text-muted-foreground">
            Organization links stored in Admin Portal.
          </p>
        </div>
        <Button
          onClick={() => navigate(RoutePaths.ORGANIZATIONS)}
          variant="outline"
        >
          Organizations
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <SummaryTile label="Total" value={data.summary.total} />
        <SummaryTile label="Linked" value={data.summary.linked} />
        <SummaryTile label="Unlinked" value={data.summary.unlinked} />
        <SummaryTile
          label="Exact matches"
          value={data.summary.exact_unlinked_matches}
        />
        <SummaryTile
          label="Sandbox inherits"
          value={data.summary.sandbox_can_inherit_from_parent}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <SummaryTile
          label="Production linked"
          value={data.summary.production_linked}
        />
        <SummaryTile
          label="Production unlinked"
          value={data.summary.production_unlinked}
        />
        <SummaryTile
          label="Sandbox linked"
          value={data.summary.sandbox_linked}
        />
        <SummaryTile
          label="Sandbox unlinked"
          value={data.summary.sandbox_unlinked}
        />
      </div>

      <CoverageTable
        filename="hubspot-unlinked-organizations.csv"
        rows={data.unlinked}
        title="Missing HubSpot links"
      />
      <CoverageTable
        filename="hubspot-exact-matches.csv"
        rows={data.exact_unlinked_matches}
        title="Exact matches available"
      />
      <CoverageTable
        filename="hubspot-sandbox-parent-inheritance.csv"
        rows={data.sandbox_can_inherit_from_parent}
        title="Sandbox links available from parent"
      />
    </div>
  )
}

export default OrganizationHubspotCoverage
